import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Flex,
  List,
  Spin,
  Input,
  Popover,
  Modal,
  Slider,
} from "antd";
import {
  CheckOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  FormOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  useDeleteChecklistMutation,
  useFetchChecklistsByTypeQuery,
  useReorderTasksMutation,
  useUpdateChecklistMutation,
} from "./checklists.service";
import { useTranslation } from "react-i18next";
import "./style.css";
import { toast } from "react-toastify";
import {
  useFetchSelfRestaurantDataQuery,
  useUpdateRestaurantSettingMutation,
} from "../restaurant/restaruant.service";
import LocationSelector from "../../components/LocationSelector"; 
import ChecklistTaskNote from "../../components/ChecklistTaskNote"; 

const OpencheckList = () => {
  const { t, i18n } = useTranslation();

  const curLanguage = i18n.language;

  const navigate = useNavigate();

    const [locationId, setLocationId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalId, setIsModalId] = useState();
  const [modalContent, setModalContent] = useState();
  const [contents, setContents] = useState({});
  const [checkTempSlider, setCheckTempSlider] = useState({});
  const [openPopoverId, setOpenPopoverId] = useState(null);

  // ✅ Fetch checklists with locationId
  const {
    data = { data: { checklists: [] } },
    isFetching,
    isSuccess,
    refetch,
  } = useFetchChecklistsByTypeQuery(
    { type: "opening", locationId },
    { refetchOnMountOrArgChange: true }
  );
  const {
    data: { checklists },
  } = data;

  // ✅ Fetch restaurant data with locationId
  const {
    data: restaurantData = {
      data: { restaurant: { openChecklistCompleted: false } },
    },
  } = useFetchSelfRestaurantDataQuery(locationId, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: {
      restaurant: { openChecklistCompleted },
    },
  } = restaurantData;

  const [deleteChecklist, { isLoading: isDeleteLoading }] =
    useDeleteChecklistMutation();
  const [requestUpdateCheckList] = useUpdateChecklistMutation();
  const [reorderTasks] = useReorderTasksMutation();
  const [requestUpdateRestaurantSetting] = useUpdateRestaurantSettingMutation();

  // ✅ Handle location change
  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
    refetch();
  };

const handleDeleteChecklist = async (id) => {
  try {
    await deleteChecklist({ 
      id: id, 
      locationId: locationId // ✅ PASS LOCATION ID
    }).unwrap();
    refetch(); // ✅ Refresh list
    toast.success("Checklist deleted successfully");
  } catch (error) {
    console.log("Failed to delete checklist", error);
    toast.error("Failed to delete checklist");
  }
};

  const handleCompleteChecklist = (data) => {
    requestUpdateCheckList(data);
  };

  const handleReorderTask = (orderTask, type) => {
    if (type === "up" && orderTask.taskNumber !== 1) {
      const tmpTaskUp = checklists.find(
        (item) => item.taskNumber === orderTask.taskNumber - 1
      );
      if (tmpTaskUp) {
        const tmpDataUp = checklists.reduce((arr, cur) => {
          if (tmpTaskUp._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber + 1 });
            return arr;
          }
          if (orderTask._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber - 1 });
            return arr;
          }
          arr.push({ id: cur._id, taskNumber: cur.taskNumber });
          return arr;
        }, []);
        reorderTasks({ tasks: tmpDataUp });
      }
    }
    if (
      type === "down" &&
      checklists.filter((item) => item.taskNumber > orderTask.taskNumber)
        .length > 0
    ) {
      const tmpTaskDown = checklists.find(
        (item) => item.taskNumber === orderTask.taskNumber + 1
      );
      if (tmpTaskDown) {
        const tmpDataDown = checklists.reduce((arr, cur) => {
          if (tmpTaskDown._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber - 1 });
            return arr;
          }
          if (orderTask._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber + 1 });
            return arr;
          }
          arr.push({ id: cur._id, taskNumber: cur.taskNumber });
          return arr;
        }, []);
        reorderTasks({ tasks: tmpDataDown });
      }
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      setIsModalId();
      setModalContent();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isSuccess) {
      setCheckTempSlider((prev) => {
        const next = { ...prev };
        checklists.forEach((cur) => {
          // Don't wipe in-progress slider edits when list refetches after ticking one row
          if (prev[cur._id] === undefined || prev[cur._id] === null) {
            next[cur._id] = cur.tempSlider;
          } else if (
            cur.status === "completed" &&
            cur.tempSlider !== undefined &&
            cur.tempSlider !== null &&
            Number(cur.tempSlider) !== 0
          ) {
            // Prefer server value once a non-zero temp was persisted
            next[cur._id] = cur.tempSlider;
          }
        });
        return next;
      });
    }
  }, [checklists]);

  return (
    <Spin spinning={isFetching}>
      <Card
      title={
          <Flex align="center" gap="middle" wrap>
            <span>{t("Opening checklist")}</span>
            {/* ✅ Location Selector */}
            <LocationSelector onLocationChange={handleLocationChange} />
          </Flex>
        }
        className="opencheck-list-page"
        bordered={false}
        extra={
          <Flex justify="center">
            <Button style={{ margin: "0 10px" }}>{t("CANCEL")}</Button>
            <Button
              onClick={() => navigate("/openchecks/create")}
              type="primary"
            >
              {t("Create")}
            </Button>
          </Flex>
        }
      >
        {/* <Spin spinning={isFetching}> */}
        <List
          bordered
          dataSource={checklists}
          renderItem={(item, key) => (
            <List.Item
              key={key}
              className={item.isSection && "background-section"}
            >
              {item.isSection ? (
                <Row className="opencheck-list-page-list-item">
                  <Col span={24} className="opencheck-list-page-title">
                    <div className="opencheck-list-page-title-description">
                      <div
                        className="compliance-checklist-content"
                        dangerouslySetInnerHTML={{
                          __html:
                            item["title"][curLanguage] ||
                            item["title"]["default"],
                        }}
                      ></div>
                    </div>
                  </Col>
                </Row>
              ) : (
                <>
                  <Row className="opencheck-list-page-list-item">
                    <Col span={20} className="opencheck-list-page-title">
                      <div className="opencheck-list-page-title-label">
                        <div
                          className="compliance-checklist-content"
                          dangerouslySetInnerHTML={{
                            __html:
                              item["title"][curLanguage] ||
                              item["title"]["default"],
                          }}
                        ></div>
                      </div>
                      <ChecklistTaskNote
                        comment={item.comment}
                        language={curLanguage}
                      />
                      <div className="opencheck-list-page-title-description">
                        {item.isInput && (
                          <div style={{ padding: "5px 10px" }}>
                            <Input
                              defaultValue={
                                item["content"][curLanguage] ||
                                item["content"]["default"]
                              }
                              style={{
                                background: "transparent",
                                maxWidth: "500px",
                              }}
                              onChange={(c) =>
                                setContents((e) => ({
                                  ...e,
                                  [item._id]: c.target.value,
                                }))
                              }
                            />
                          </div>
                        )}
                        {item.isDateRecord && (
                          <div style={{ padding: "0 10px" }}>
                            *RECORD TIME AND DATE AUTOMATICALLY*
                          </div>
                        )}
                        {item.isTempSlider && (
                          <>
                            <div
                              style={{
                                fontSize: "1.5rem",
                                textAlign: "center",
                                marginTop: 10,
                                fontWeight: "bold",
                              }}
                            >
                              {checkTempSlider[item._id] != null
                                ? Number(checkTempSlider[item._id]).toFixed(1)
                                : "0.0"}
                              °C
                            </div>
                            <Slider
                              className="temp-slider-width"
                              min={-30}
                              max={120}
                              step={0.1}
                              styles={{
                                track: {
                                  background: "transparent",
                                },
                                tracks: {
                                  background: `rgb(109, 179, 63)`,
                                },
                              }}
                              onChange={(temp) =>
                                setCheckTempSlider((e) => ({
                                  ...e,
                                  [item._id]: temp,
                                }))
                              }
                              value={
                                checkTempSlider[item._id] != null
                                  ? checkTempSlider[item._id]
                                  : 0
                              }
                            />
                          </>
                        )}
                      </div>
                    </Col>
                    <Col span={2} className="opencheck-list-page-complete">
                      {item.status === "completed" ? (
                        <CheckOutlined
                          className="checklist-complete"
                          onClick={() => {
                            if (openChecklistCompleted) {
                              toast.error(
                                "You cannot change the status of a completed checklist."
                              );
                              return;
                            }
                            handleCompleteChecklist({
                              id: item._id,
                              data: {
                                status: "pending",
                                content:
                                  contents[item._id] ??
                                  (item["content"][curLanguage] ||
                                    item["content"]["default"]),
                                tempSlider:
                                  checkTempSlider[item._id] ?? item.tempSlider,
                              },
                            });
                          }}
                        />
                      ) : item.comment.default !== "" ? (
                        <SwapOutlined
                          className="checklist-complete"
                          onClick={() => {
                            if (openChecklistCompleted) {
                              toast.error(
                                "You cannot change the status of a completed checklist."
                              );
                              return;
                            }
                            handleCompleteChecklist({
                              id: item._id,
                              data: {
                                comment: "",
                                content:
                                  contents[item._id] ??
                                  (item["content"][curLanguage] ||
                                    item["content"]["default"]),
                                tempSlider:
                                  checkTempSlider[item._id] ?? item.tempSlider,
                              },
                            });
                          }}
                        />
                      ) : (
                        <CheckOutlined
                          className="checklist-pending"
                          onClick={() => {
                            if (openChecklistCompleted) {
                              toast.error(
                                "You cannot change the status of a completed checklist."
                              );
                              return;
                            }

                            // if it’s an input field, ensure there’s some content
                            if (item.isInput) {
                              const userContent = contents[item._id]?.trim();
                              const existingContent = (
                                (item["content"][curLanguage] ||
                                  item["content"]["default"]) ??
                                ""
                              ).trim();
                              if (!userContent && !existingContent) {
                                toast.error(
                                  "Please enter content before completing."
                                );
                                return;
                              }
                            }

                            handleCompleteChecklist({
                              id: item._id,
                              data: {
                                status: "completed",
                                content:
                                  contents[item._id] ??
                                  (item["content"][curLanguage] ||
                                    item["content"]["default"]),
                                tempSlider:
                                  checkTempSlider[item._id] ?? item.tempSlider,
                              },
                            });
                          }}
                        />
                      )}
                    </Col>
                    <Col span={2} className="opencheck-list-page-tools">
                      <Popover
                        placement="bottom"
                        content={
                          <div className="opencheck-list-page-tools-popover">
                            <Button
                              color="cyan"
                              variant="solid"
                              style={{ background: "rgb(47, 45, 113)" }}
                              onClick={() => {
                                handleReorderTask(item, "up");
                                setOpenPopoverId(null);
                              }}
                            >
                              {t("MoveUp")}
                            </Button>
                            <Button
                              color="cyan"
                              variant="solid"
                              style={{ background: "rgb(47, 45, 113)" }}
                              onClick={() => {
                                handleReorderTask(item, "down");
                                setOpenPopoverId(null);
                              }}
                            >
                              {t("MoveDown")}
                            </Button>
                            {item.comment.default === "" && (
                              <Button
                                color="green"
                                variant="solid"
                                icon={<EditOutlined />}
                                onClick={() =>
                                  navigate(`/openchecks/edit/${item._id}`)
                                }
                                size="small"
                              >
                                {t("Edit")}
                              </Button>
                            )}
                            <Button
                              variant="solid"
                              color="danger"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                handleDeleteChecklist(item._id);
                                setOpenPopoverId(null);
                              }}
                              loading={isDeleteLoading}
                            >
                              {t("DELETE")}
                            </Button>
                            {item.status !== "completed" &&
                              item.comment.default === "" && (
                                <Button
                                  variant="solid"
                                  color="pink"
                                  size="small"
                                  icon={<FormOutlined />}
                                  onClick={() => {
                                    setIsModalOpen(true);
                                    setIsModalId(item._id);
                                    setOpenPopoverId(null);
                                  }}
                                >
                                  {t("Add Note")}
                                </Button>
                              )}
                          </div>
                        }
                        open={openPopoverId === item._id}
                        onOpenChange={(visible) =>
                          setOpenPopoverId(visible ? item._id : null)
                        }
                        trigger="click"
                      >
                        <MoreOutlined className="opencheck-list-page-tools-icon" />
                      </Popover>
                    </Col>
                  </Row>
                </>
              )}
            </List.Item>
          )}
        />
        <Flex justify="center" style={{ marginTop: "10px" }}>
          <Button
            type="primary"
            disabled={
              checklists.filter((item) => !item.isSection).length ===
              checklists.filter(
                (item) =>
                  !item.isSection &&
                  (item.status === "completed" || item.comment.default !== "")
              ).length
                ? false
                : true
            }
            onClick={async () => {
              if (
                checklists.filter((item) => !item.isSection).length ===
                checklists.filter(
                  (item) =>
                    !item.isSection &&
                    (item.status === "completed" || item.comment.default !== "")
                ).length
              ) {
                try {
                  await Promise.all(
                    checklists
                      .filter((item) => item.isTempSlider)
                      .map((item) =>
                        requestUpdateCheckList({
                          id: item._id,
                          data: {
                            status: item.status,
                            tempSlider:
                              checkTempSlider[item._id] ??
                              item.tempSlider ??
                              0,
                          },
                          locationId,
                        }).unwrap()
                      )
                  );
                } catch (e) {
                  console.log("Failed to persist temperatures before submit", e);
                }
                requestUpdateRestaurantSetting({
                  openChecklistCompleted: true,
                  openChecklistCompletedAt: new Date().getTime(),
                });
                navigate("/home");
              }
            }}
          >
            {t("Submit")}
          </Button>
        </Flex>
        <Modal
          title="Reason"
          open={isModalOpen}
          onOk={() => {
            if (modalContent && modalContent !== "") {
              handleCompleteChecklist({
                id: isModalId,
                data: {
                  comment: modalContent,
                  ...(checkTempSlider[isModalId] != null && {
                    tempSlider: checkTempSlider[isModalId],
                  }),
                },
              });
              setIsModalOpen(false);
            } else {
              navigate("/home");
            }
          }}
          onCancel={() => setIsModalOpen(false)}
        >
          <Input.TextArea
            onChange={(e) => setModalContent(e.target.value)}
            value={modalContent}
            autoSave={{ min: 3, max: 3 }}
          />
        </Modal>
        {/* </Spin> */}
      </Card>
    </Spin>
  );
};

export default OpencheckList;
